import React from "react";
import { motion } from "framer-motion";
import { Star, Quote, Download, FileText, ExternalLink } from "lucide-react";
import { PDF_ADULTE, PDF_ENFANT, GOOGLE_REVIEW_FOUGERES, GOOGLE_REVIEW_LOUVIGNE } from "../lib/utils";

const REVIEWS = [
  {
    name: "Jamie",
    role: "Sortie entre amis",
    text: "Super expérience ! L'équipe est très accueillante, le matériel impeccable et les terrains vraiment fun. On a passé un excellent moment, à recommander sans hésiter pour un EVG dans le 35.",
    rating: 5,
  },
  {
    name: "Claire",
    role: "Anniversaire enfant",
    text: "J'ai organisé l'anniversaire de mon fils, les enfants étaient ravis ! L'encadrement est top, sécurité au rendez-vous, et les forfaits juniors sont parfaits. Une vraie réussite à Louvigné-du-Désert.",
    rating: 5,
  },
  {
    name: "Elise",
    role: "EVJF",
    text: "Une après-midi inoubliable pour mon EVJF ! Cadre en forêt magnifique, ambiance au top, fou rires garantis. Merci à toute l'équipe pour leur professionnalisme et leur bonne humeur.",
    rating: 5,
  },
  {
    name: "Aurélie",
    role: "Comité d'entreprise",
    text: "Excellente activité team building pour notre comité d'entreprise à Fougères. Les nouveaux terrains sont superbes, parfaitement sécurisés. Le groupe a adoré, on reviendra c'est sûr !",
    rating: 5,
  },
];

export default function Reviews() {
  return (
    <section
      id="avis"
      data-testid="reviews-section"
      className="relative bg-white py-20 md:py-28 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              05 — Avis & Réassurance
            </p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase leading-[0.95] tracking-tight">
              Ils ont vécu
              <br />
              <span className="text-[#E91E63]">l&apos;aventure</span>
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-5 h-5 fill-[#FFEB3B] text-[#FFEB3B]"
                  strokeWidth={1.5}
                />
              ))}
            </div>
            <div>
              <p className="font-display text-xl font-bold leading-none">
                5,0 / 5
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Avis clients vérifiés
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-gray-200 border-2 border-black">
          {REVIEWS.map((r, i) => (
            <motion.figure
              key={r.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              data-testid={`review-card-${r.name.toLowerCase().replace(/\s/g, "-")}`}
              className="bg-white p-6 md:p-8 lg:p-10 flex flex-col h-full"
            >
              <Quote
                className="w-8 h-8 text-[#E91E63] mb-4"
                strokeWidth={2}
                fill="#E91E63"
              />
              <blockquote className="text-base md:text-lg text-gray-800 leading-relaxed mb-6 flex-1">
                « {r.text} »
              </blockquote>
              <figcaption className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="font-display font-bold uppercase text-base">
                    {r.name}
                  </p>
                  <p className="text-xs text-gray-500 font-mono uppercase tracking-wider mt-0.5">
                    {r.role}
                  </p>
                </div>
                <div className="flex items-center gap-0.5">
                  {[...Array(r.rating)].map((_, j) => (
                    <Star
                      key={j}
                      className="w-4 h-4 fill-[#FFEB3B] text-[#FFEB3B]"
                      strokeWidth={1.5}
                    />
                  ))}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* Google Review CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          data-testid="google-review-block"
          className="mt-12 md:mt-16 grid lg:grid-cols-12 border-2 border-black overflow-hidden"
        >
          <div className="lg:col-span-5 bg-[#E91E63] text-white p-6 md:p-10 lg:p-12 flex flex-col justify-center">
            <div className="inline-flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-5 h-5 fill-[#FFEB3B] text-[#FFEB3B]"
                  strokeWidth={1.5}
                />
              ))}
            </div>
            <p className="font-mono text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-[#FFEB3B] mb-2">
              Vous nous avez aimés ?
            </p>
            <h3 className="font-display text-3xl md:text-4xl font-bold uppercase leading-tight tracking-tight">
              Laissez-nous<br />un avis Google
            </h3>
            <p className="mt-4 text-white/90 text-sm md:text-base max-w-md">
              Votre retour aide d&apos;autres groupes à découvrir Paintball Aventures 35. Choisissez le site où vous avez joué — 2 clics suffisent !
            </p>
          </div>
          <div className="lg:col-span-7 bg-white p-6 md:p-10 lg:p-12 flex flex-col justify-center gap-4">
            <a
              href={GOOGLE_REVIEW_FOUGERES}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="review-link-fougeres"
              className="group flex items-center justify-between gap-4 px-5 py-5 border-2 border-black hover:bg-[#FFEB3B] transition-colors"
            >
              <div className="flex items-center gap-4">
                <span className="font-display text-2xl md:text-3xl font-bold leading-none">
                  Fougères
                </span>
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#E91E63]">
                  → Noter sur Google
                </span>
              </div>
              <ExternalLink
                className="w-5 h-5 text-black group-hover:translate-x-1 transition-transform"
                strokeWidth={2.5}
              />
            </a>
            <a
              href={GOOGLE_REVIEW_LOUVIGNE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="review-link-louvigne"
              className="group flex items-center justify-between gap-4 px-5 py-5 border-2 border-black hover:bg-[#FFEB3B] transition-colors"
            >
              <div className="flex items-center gap-4 min-w-0">
                <span className="font-display text-2xl md:text-3xl font-bold leading-none">
                  Louvigné-du-Désert
                </span>
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#E91E63] whitespace-nowrap">
                  → Noter sur Google
                </span>
              </div>
              <ExternalLink
                className="w-5 h-5 text-black group-hover:translate-x-1 transition-transform shrink-0"
                strokeWidth={2.5}
              />
            </a>
          </div>
        </motion.div>

        {/* Downloads */}
        <div
          id="documents"
          className="mt-12 md:mt-16 border-2 border-black bg-[#fafafa] p-6 md:p-10"
          data-testid="downloads-block"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <FileText
                className="w-10 h-10 text-[#E91E63] shrink-0 mt-1"
                strokeWidth={2}
              />
              <div>
                <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-1">
                  Documents à télécharger
                </p>
                <h3 className="font-display text-2xl md:text-3xl font-bold uppercase leading-tight">
                  Autorisations parentales
                </h3>
                <p className="text-sm text-gray-600 mt-2 max-w-md">
                  À remplir et signer avant votre venue sur le terrain paintball forêt — obligatoire pour tous les mineurs et conseillé pour les adultes.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <a
                href={PDF_ADULTE}
                target="_blank"
                rel="noopener noreferrer"
                download="attestation_adulte_paintball_aventures_35.pdf"
                data-testid="download-adult-pdf"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-black text-white font-bold uppercase text-xs md:text-sm tracking-wider hover:bg-[#E91E63] transition-colors"
              >
                <Download className="w-4 h-4" strokeWidth={2.5} />
                Autorisation adultes
              </a>
              <a
                href={PDF_ENFANT}
                target="_blank"
                rel="noopener noreferrer"
                download="attestation_enfant_paintball_aventures_35.pdf"
                data-testid="download-children-pdf"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#E91E63] text-white font-bold uppercase text-xs md:text-sm tracking-wider hover:-translate-y-0.5 hover:shadow-[0_6px_0_-2px_#0a0a0a] transition-all"
              >
                <Download className="w-4 h-4" strokeWidth={2.5} />
                Autorisation enfants
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
